import { useCallback, useState } from "react";
import HeroSlider from "./HeroSlider";
import HeroVideo from "./HeroVideo";
import CyberSecSlider from "./CyberSecSlider";
import "./Hero1.css";
import HeroFilters from "./Herofilter";

// Cybersecurity Awareness Month expiry — mirrors the constant in CyberSecSlider.jsx.
// Hero.jsx checks it here so the reel step is skipped entirely once expired,
// keeping the video → main-slider loop clean with zero dead frames.
const CYBER_EXPIRY = new Date("2026-10-13T23:59:59");
const cyberActive = () => Date.now() <= CYBER_EXPIRY.getTime();

// The hero is a single continuous reel:
//   intro video
//     -> [Cybersecurity Awareness slider — Week 1, until 13 Oct 2026]
//     -> main promotional slides (ending with the Omoka na Zetech banner)
//     -> back to the video -> and so on.
//
// The left/right arrows drive the same reel in both directions, so the
// visitor can step back from the first cybersec slide into the video, or
// step forward from the video into the cybersec slider (or straight to the
// main slider once the campaign expires).
//
// stage values:
//   "video"  — HeroVideo is on screen
//   "cyber"  — CyberSecSlider is on screen (only when campaign is live)
//   "slides" — HeroSlider (main promo) is on screen

export default function Hero() {
    const [stage, setStage] = useState("video");

    // Which slide the main HeroSlider should open on.
    // -1 means "the last slide" (stepping backwards through the reel).
    const [entrySlide, setEntrySlide] = useState(0);

    // Remount keys ensure each component restarts cleanly when re-entered.
    const [videoRun,  setVideoRun]  = useState(0);
    const [cyberRun,  setCyberRun]  = useState(0);
    const [sliderRun, setSliderRun] = useState(0);

    // ── Helpers ──────────────────────────────────────────────────────────────

    const openCyber = useCallback(() => {
        setCyberRun((n) => n + 1);
        setStage("cyber");
    }, []);

    const openSlider = useCallback((index) => {
        setEntrySlide(index);
        setSliderRun((n) => n + 1);
        setStage("slides");
    }, []);

    const backToVideo = useCallback(() => {
        setVideoRun((n) => n + 1);
        setStage("video");
    }, []);

    // ── Video callbacks ───────────────────────────────────────────────────────

    // Video finished / skipped -> next in reel (cyber if active, else main slider).
    const handleVideoEnd = useCallback(() => {
        if (cyberActive()) { openCyber(); } else { openSlider(0); }
    }, [openCyber, openSlider]);

    const handleVideoNext = useCallback(() => {
        if (cyberActive()) { openCyber(); } else { openSlider(0); }
    }, [openCyber, openSlider]);

    // Stepping backwards from the video wraps to the last main slide.
    const handleVideoPrev = useCallback(() => {
        openSlider(-1);
    }, [openSlider]);

    // ── CyberSec slider callbacks ─────────────────────────────────────────────

    // Finished all cybersec slides -> hand off to main slider.
    const handleCyberEnd = useCallback(() => {
        openSlider(0);
    }, [openSlider]);

    // Stepped backwards off the first cybersec slide -> back to video.
    const handleCyberRequestVideo = useCallback(() => {
        backToVideo();
    }, [backToVideo]);

    // ── Render ────────────────────────────────────────────────────────────────

    const isVideo  = stage === "video";
    const isCyber  = stage === "cyber";
    const isSlider = stage === "slides";

    return (
        <>
            <section className={`hero ${isVideo ? "hero--video" : "hero--slider"}`}>

                {isVideo && (
                    <HeroVideo
                        key={videoRun}
                        onVideoEnd={handleVideoEnd}
                        onNext={handleVideoNext}
                        onPrev={handleVideoPrev}
                    />
                )}

                {isCyber && (
                    <CyberSecSlider
                        key={cyberRun}
                        onCycleEnd={handleCyberEnd}
                        onRequestVideo={handleCyberRequestVideo}
                    />
                )}

                {isSlider && (
                    <HeroSlider
                        key={sliderRun}
                        initialIndex={entrySlide}
                        onCycleEnd={backToVideo}
                        onRequestVideo={backToVideo}
                    />
                )}

                <div className="overlay"></div>
            </section>

            <HeroFilters />
        </>
    );
}
